// src/components/black-friday/main-app-migration-modal.tsx
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, useUser } from "@clerk/clerk-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Info, ArrowRight } from "lucide-react";
import { useBFAccounts } from "@/contexts/black-friday-account-context";

/**
 * Modal that detects when a user from the main app lands on BF without any BF accounts configured
 * Offers to help them set up Black Friday tracking for their existing accounts
 */
export function MainAppMigrationModal() {
  const navigate = useNavigate();
  const { isSignedIn } = useAuth();
  const { user } = useUser();
  const { accounts: bfAccounts } = useBFAccounts();
  const [showModal, setShowModal] = useState(false);
  const [mainAppAccounts, setMainAppAccounts] = useState<any[]>([]);

  useEffect(() => {
    // Only show if:
    // 1. User is signed in
    // 2. User has NO BF accounts configured OR current active account not in BF
    // 3. User came from main app (has main app accounts in Clerk metadata)
    // 4. Haven't dismissed this modal before
    if (!isSignedIn || !user) return;

    const dismissed = sessionStorage.getItem("bf_migration_modal_dismissed");
    if (dismissed === "true") return;

    // Check if user has main app accounts
    const mainAccounts = user.unsafeMetadata?.accounts as any[] | undefined;
    if (!mainAccounts || mainAccounts.length === 0) return;

    // Show modal if user has NO BF accounts
    if (bfAccounts.length === 0) {
      setMainAppAccounts(mainAccounts);
      setShowModal(true);
      return;
    }

    // OR show if user has a currently active account that's NOT in BF
    const activeAccountId = user.unsafeMetadata?.activeDexieId as string | undefined;
    if (activeAccountId) {
      const currentAccount = mainAccounts.find((acc: any) => acc.dexieId === activeAccountId);
      if (currentAccount) {
        const accountCID = currentAccount.accountCID || currentAccount.customerId;
        if (accountCID) {
          const isInBFAccounts = bfAccounts.some((bfAcc) => bfAcc.accountCID === accountCID);
          if (!isInBFAccounts) {
            // Current account not in BF - but don't show modal, the banner will handle this
            return;
          }
        }
      }
    }
  }, [isSignedIn, user, bfAccounts.length]);

  const handleSetup = () => {
    setShowModal(false);
    navigate("/bf/setup");
  };

  const handleDismiss = () => {
    sessionStorage.setItem("bf_migration_modal_dismissed", "true");
    setShowModal(false);
  };

  const handleGoBack = () => {
    navigate("/");
  };

  return (
    <Dialog open={showModal} onOpenChange={setShowModal}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="text-2xl">Welcome to Black Friday Tracking!</DialogTitle>
          <DialogDescription>
            You're using 8020agent with {mainAppAccounts.length} account{mainAppAccounts.length !== 1 ? "s" : ""}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <Alert>
            <Info className="h-4 w-4" />
            <AlertDescription>
              <strong>Black Friday tracking uses a separate Google Sheet</strong> from your main 8020agent data.
              This allows you to track historical Black Friday performance (2023, 2024, 2025) without affecting your regular reports.
            </AlertDescription>
          </Alert>

          <div>
            <h4 className="font-semibold mb-2">Your Main App Accounts:</h4>
            <div className="space-y-2">
              {mainAppAccounts.slice(0, 5).map((account, idx) => (
                <div key={idx} className="text-sm bg-muted p-2 rounded">
                  {account.name || account.accountName || "Unnamed Account"}
                  {account.accountCID && <span className="text-muted-foreground ml-2">({account.accountCID})</span>}
                </div>
              ))}
              {mainAppAccounts.length > 5 && (
                <div className="text-sm text-muted-foreground">
                  ...and {mainAppAccounts.length - 5} more
                </div>
              )}
            </div>
          </div>

          <div>
            <h4 className="font-semibold mb-2">To track Black Friday for these accounts:</h4>
            <ol className="list-decimal list-inside space-y-2 text-sm">
              <li>Run the Black Friday script in each Google Ads account</li>
              <li>Copy the Web App URL from the generated Google Sheet</li>
              <li>Add each account here with its Black Friday Web App URL</li>
            </ol>
          </div>

          <Alert variant="default" className="bg-blue-50 border-blue-200">
            <Info className="h-4 w-4 text-blue-600" />
            <AlertDescription className="text-blue-900">
              <strong>Don't worry!</strong> This won't affect your existing 8020agent data.
              Black Friday tracking is completely separate and optional.
            </AlertDescription>
          </Alert>
        </div>

        <DialogFooter className="flex gap-2">
          <Button variant="outline" onClick={handleGoBack}>
            Go Back to Main App
          </Button>
          <Button variant="outline" onClick={handleDismiss}>
            Dismiss
          </Button>
          <Button onClick={handleSetup}>
            Set Up Black Friday Tracking
            <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
